import { Alert, Box, Button, FormControl, LinearProgress } from "@mui/material";
import { useEffect, useState } from "react";

import { useTenantProfile } from "@/common/hooks/entity/tenant/useTenantProfile";
import { DamageDetectionSendToExternalDamageCostEvaluationInlineDto } from "@/core/api/generated/v1";

import AppModal, { AppModalProps } from "../../Modals/AppModal";
import AppModalActions from "../../Modals/AppModalActions";
import AppModalContent from "../../Modals/AppModalContent";
import TenantAutocomplete from "../Tenant/TenantAutocomplete";

interface OwnProps {
  defaultValues: Nil<DamageDetectionSendToExternalDamageCostEvaluationInlineDto>;
  onSave: (newValue: Nil<DamageDetectionSendToExternalDamageCostEvaluationInlineDto>) => void;
}

type Props = OwnProps & AppModalProps;

export default function DamageDetectionSendToExternalDamageCostEvaluationInlineInputModal({
  defaultValues,
  onSave,
  ...modalProps
}: Props) {
  const tenantProfile = useTenantProfile();

  const [values, setValues] =
    useState<Nil<DamageDetectionSendToExternalDamageCostEvaluationInlineDto>>(defaultValues);

  const isInited = !!tenantProfile;
  const isDefaultAssessorCompanySelected =
    !!values?.assessorTenantId &&
    values?.assessorTenantId == tenantProfile?.settings?.tenantToTenant?.defaultAssessorTenantId;

  // auto-select default assessor company
  useEffect(() => {
    if (
      tenantProfile?.settings?.tenantToTenant?.defaultAssessorTenantId &&
      !values?.assessorTenantId
    ) {
      setValues({
        ...values,
        assessorTenantId: tenantProfile?.settings?.tenantToTenant?.defaultAssessorTenantId,
      });
    }
  }, [tenantProfile]);

  return (
    <AppModal
      maxWidth='md'
      fullWidth
      keepMounted={false}
      titleProps={{
        title: "Configure details",
      }}
      {...modalProps}
      onClose={(event, reason) => {
        modalProps.onClose && modalProps.onClose(event, reason);
      }}
    >
      {(params) => (
        <AppModalContent>
          {!isInited && <LinearProgress />}

          {isInited && (
            <Box>
              {isDefaultAssessorCompanySelected && (
                <Alert severity='info' sx={{ mb: 1 }}>
                  Default assessor company is selected.
                </Alert>
              )}

              <FormControl margin='normal' fullWidth>
                <TenantAutocomplete
                  entityId={values?.assessorTenantId}
                  searchFilters={{
                    isInMyConnections: true,
                  }}
                  required={false}
                  label='Assessor company'
                  onChange={(newValue) => {
                    setValues({
                      ...values,
                      assessorTenantId: newValue?.id,
                    });
                  }}
                />
              </FormControl>
            </Box>
          )}

          <AppModalActions
            sx={{ mt: 2 }}
            modalProps={{
              onClose: params.close,
            }}
            isAutoCloseOnSubmit
            isAutoCloseOnCancel
            submitButton={
              <Button
                variant='contained'
                color='primary'
                size='medium'
                onClick={() => {
                  onSave(values);
                }}
              >
                Save
              </Button>
            }
            cancelButton={
              <Button fullWidth variant='outlined' color='text'>
                Cancel
              </Button>
            }
          ></AppModalActions>
        </AppModalContent>
      )}
    </AppModal>
  );
}
